import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  OrdersTable,
  getOrderColumns,
} from "../../../components/dashboard/orders/orders-table";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getOrdersAsync } from "../../../slices/order";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowPathRoundedSquareIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { mapErrors, updateUrlWithException } from "../../../components/Utils";
import { syncModule } from "../../../api/company";
import SimplePrompt from "../../../components/utils/simplePrompt";
import { debounce } from "lodash";
import {
  orderStatusOptions,
  OrderStatus,
} from "../../../components/dashboard/orders/utils";
import { CustomerTitle, ProjectTitle } from "../../../components/Utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import { useSettings } from "../../../contexts/settings-context";
import { BulkBar } from "../../../components/utils/BulkBar";
import { TableToolbar } from "../../../components/utils/TableToolbar";
import { useTableView } from "../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../components/utils/PMTableView/RightDrawer";

const toISODate = (date) => format(date, "yyyy-MM-dd");

const parseFilterValue = (filter) => {
  if (!filter) return [];
  try {
    const parsed = JSON.parse(filter.value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const OrdersList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { priceFormatter } = useSettings();

  const defaultPeriod = useMemo(() => {
    const today = new Date();
    return {
      periodType: "month",
      from: toISODate(startOfMonth(today)),
      to: toISODate(endOfMonth(today)),
    };
  }, []);
  const initialOpenOrders = searchParams.get("view") !== "all";

  // Initialize filters from URL (restorable links) or the open-orders default
  const getInitialFiltersFromUrl = () => {
    const filtersParam = searchParams.get("filters");
    if (filtersParam) {
      try {
        return JSON.parse(filtersParam);
      } catch (e) {
        console.error("Error parsing filters from URL:", e);
      }
    }
    return initialOpenOrders
      ? [{ name: "view", value: JSON.stringify(["active"]) }]
      : [];
  };

  const [controller, setController] = useState({
    filters: getInitialFiltersFromUrl(),
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    per_page: searchParams.get("per_page") ? searchParams.get("per_page") : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "DESC",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "order_number",
    view: initialOpenOrders ? "active" : "all",
    period_type: searchParams.get("period_type") || defaultPeriod.periodType,
    from_date: searchParams.get("from_date") || defaultPeriod.from,
    to_date: searchParams.get("to_date") || defaultPeriod.to,
    fields: "id,order_number,order_date,customer,project,status,total_amount,tripletex_sync",
  });

  const itemState = useSelector((store) => {
    return store.orders;
  });

  // Persist all controller params (including filters) to the URL
  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
        period_type: controller.period_type,
        from_date: controller.from_date,
        to_date: controller.to_date,
        filters:
          controller.filters && controller.filters.length > 0
            ? JSON.stringify(controller.filters)
            : null,
      },
      "filters"
    );
  }, [controller]);

  const { isLoading } = itemState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [drawerOrder, setDrawerOrder] = useState(null);
  const [activeView, setActiveView] = useState(initialOpenOrders ? "active" : "all");

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  // ── Table-view state (columns, grouping, density, saved views) ───────────
  const columns = useMemo(
    () => getOrderColumns(t, priceFormatter),
    [t, priceFormatter]
  );
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "orders",
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      view: activeView,
    }),
    applySnapshot: (snap) => {
      setActiveView(snap.view || "all");
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = {
    field: controller.sortBy,
    dir: String(controller.sort).toLowerCase() === "asc" ? "asc" : "desc",
  };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort:
        c.sortBy === field && String(c.sort).toLowerCase() === "asc"
          ? "DESC"
          : "ASC",
    }));
  };

  const orderViews = [
    { id: "active", name: t("Åpne ordre") },
    { id: "all", name: t("All") },
  ];
  const handleViewChange = (viewId) => {
    setActiveView(viewId);
    setController((c) => ({
      ...c,
      page: 1,
      view: viewId,
      filters:
        viewId === "active"
          ? [{ name: "view", value: JSON.stringify(["active"]) }]
          : [],
    }));
  };

  // Infinite scroll: load the next page and append results
  const handleLoadMore = () => {
    const nextPage = (parseInt(controller.page, 10) || 1) + 1;
    skipNextDispatchRef.current = true;
    const payload = { ...controller, page: nextPage, append: true };
    const viewFilter = payload.filters.find((f) => f.name === "view");
    if (parseFilterValue(viewFilter).includes("active")) {
      delete payload.from_date;
      delete payload.to_date;
      delete payload.period_type;
    }
    dispatch(getOrdersAsync(payload));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const buildInitialModuleFilters = () => {
    const viewFilter = controller.filters.find((filter) => filter.name === "view");
    const statusFilter = controller.filters.find((filter) => filter.name === "status");
    const selectedViews = parseFilterValue(viewFilter);

    return {
      query: controller.query || "",
      active: selectedViews.includes("active"),
      statuses: parseFilterValue(statusFilter),
    };
  };

  const isOpenOrdersSelected = buildInitialModuleFilters().active;

  const handleModuleFilterChange = (filters) => {
    const nextFilters = [];
    const openOrders = filters.active === true;

    if (openOrders) {
      nextFilters.push({
        name: "view",
        value: JSON.stringify(["active"]),
      });
    }

    if (filters.statuses && filters.statuses.length > 0) {
      nextFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }

    setController((prev) => ({
      ...prev,
      page: 1,
      query: filters.query || "",
      view: openOrders ? "active" : "all",
      filters: nextFilters,
    }));
  };

  const handlePeriodChange = (period) => {
    setController((prev) => ({
      ...prev,
      page: 1,
      period_type: period.periodType,
      from_date: period.from,
      to_date: period.to,
    }));
  };

  const debouncedDispatchAction = useMemo(
    () =>
      debounce((nextController) => {
        const payload = { ...nextController };
        const viewFilter = payload.filters.find((filter) => filter.name === "view");
        const selectedViews = parseFilterValue(viewFilter);
        if (selectedViews.includes("active")) {
          delete payload.from_date;
          delete payload.to_date;
          delete payload.period_type;
        }
        dispatch(getOrdersAsync(payload));
      }, 250),
    [dispatch]
  );

  useEffect(() => {
    // Skip when handleLoadMore already dispatched for this controller update
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, debouncedDispatchAction]);

  const [, setSyncState] = useState({
    isLoading: false,
    error: false,
    errors: [],
    updated: 0,
    created: 0,
    found: 0,
  });

  const runOrderSync = () => {
    return syncModule({ orders: true })
      .then((res) => {
        const result = res.data.orders || {};
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: result.updated || 0,
          created: result.created || 0,
          found: result.found || 0,
        }));
        toast.success(
          `${result.updated || 0} was updated out of ${result.found || 0} - ${result.created || 0} was created`
        );
        return res;
      })
      .catch((err) => {
        // Updating ItemState
        setSyncState((prevState) => ({
          ...prevState,
          isLoading: false,
          updated: 0,
          created: 0,
          found: 0,
        }));
        return err;
      });
  };

  const SyncOrders = () => {
    setSyncState({
      isLoading: true,
      error: false,
      errors: [],
      updated: 0,
      created: 0,
      found: 0,
    });

    toast.promise(runOrderSync(), {
      loading: t("Syncing...."),
      success: (res) => {
        if (res.data.errors) throw new Error(mapErrors(res.data.errors));
        if (res.status === 500) throw new Error("Server error");
        return `Sync was successfull`;
      },
      error: t("Something went wrong. Could not save data!"),
    });
  };

  const [syncPromptState, setSyncPromptState] = useState(false);

  const headerActions = [
    {
      component: null,
      label: "Sync API",
      description: "",
      path: null,
      action: () => setSyncPromptState(true),
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
  ];

  const statusFilterOptions = orderStatusOptions.map((option) => ({
    value: option.value,
    label: t(option.label),
    class: "bg-slate-100 text-slate-800",
  }));

  const listCount = itemState.totalCount != null ? `${itemState.totalCount}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        subtitle=""
        actions={headerActions}
        title={t("Orders")}
        listCount={listCount}
        meta={[]}
        mainButton={{
          label: t("New"),
          action: null,
          path: "/dashboard/orders/new",
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="p-5 space-y-4">
          {/* Filter card */}
          <ModuleFilter
            filterCard
            disabled={isLoading}
            onFiltersChange={handleModuleFilterChange}
            initialFilters={buildInitialModuleFilters()}
            searchPlaceholder={t("Search orders...")}
            hideStatusFilter={true}
            selectedItems={selectedItems}
            customCheckboxButtonFilters={[
              {
                key: "active",
                label: t("Åpne ordre"),
                defaultValue: true,
              },
            ]}
            customDropdownFilters={[
              {
                key: "statuses",
                label: t("Status"),
                options: statusFilterOptions,
              },
            ]}
          />

          {!isOpenOrdersSelected && (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <PeriodSelector
                periodType={controller.period_type}
                from={controller.from_date}
                to={controller.to_date}
                onChange={handlePeriodChange}
                allowedPeriodTypes={[
                  "day",
                  "week",
                  "month",
                  "quarter",
                  "year",
                  "custom",
                ]}
              />
            </div>
          )}

          {/* Table card */}
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
            <TableToolbar
              views={orderViews}
              activeView={activeView}
              onViewChange={handleViewChange}
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <OrdersTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              density={tableView.density}
              hasMore={(itemState.totalCount || 0) > (itemState.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onItemsSelect={(items) => setSelectedItems(items)}
              onRowClick={(order) => setDrawerOrder(order)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Floating bulk action bar */}
      <BulkBar
        count={selectedItems.length}
        onClear={() => setSelectedItems([])}
        actions={[
          {
            label: t("Edit"),
            icon: PencilSquareIcon,
            action: () => toast.error(t("Kommer snart")),
          },
          { separator: true },
          {
            label: t("Delete"),
            icon: TrashIcon,
            danger: true,
            action: () => toast.error(t("Kommer snart")),
          },
        ]}
      />

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerOrder}
        onClose={() => setDrawerOrder(null)}
        eyebrow={drawerOrder ? `${t("order.number")} ${drawerOrder.order_number || ""}` : ""}
        title={
          drawerOrder?.customer ? (
            <CustomerTitle customer={drawerOrder.customer} />
          ) : (
            t("Orders")
          )
        }
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/orders/${drawerOrder.id}`),
          },
        ]}
      >
        {drawerOrder && (
          <DrawerSection>
            <DrawerProps
              items={[
                { label: t("status"), value: <OrderStatus order={drawerOrder} /> },
                {
                  label: t("customer"),
                  value: <CustomerTitle customer={drawerOrder.customer} />,
                },
                {
                  label: t("project"),
                  value: drawerOrder.project ? (
                    <ProjectTitle project={drawerOrder.project} />
                  ) : (
                    "—"
                  ),
                },
                { label: t("order.date"), value: drawerOrder.order_date || "—" },
                {
                  label: t("order.total"),
                  value: priceFormatter
                    ? priceFormatter.format(drawerOrder.total_amount || 0)
                    : drawerOrder.total_amount,
                },
              ]}
            />
          </DrawerSection>
        )}
      </RightDrawer>

      <SimplePrompt
        title="Sync av ordre"
        description="Er du sikker på at du ønsker å synke ordrer nå?"
        onAccept={() => SyncOrders()}
        open={syncPromptState}
        onClose={() => setSyncPromptState(false)}
      />
    </>
  );
};
